import { useState } from "react";

/**
 * Der Einfügepunkt zwischen zwei Zeilen.
 *
 * Er steht immer im Markup, wird aber erst beim Zeigen sichtbar — eine
 * dauerhaft eingeblendete Reihe von Plus-Knöpfen zerschnitte die Liste
 * optisch in Streifen. Ohne ihn musste eine vergessene Position unten
 * angelegt und dann Schritt für Schritt hochgeklickt werden.
 *
 * Der Punkt hält sich offen, solange er den Fokus hat: sonst verschwände er
 * beim Tabben dorthin, bevor man ihn auslösen kann.
 */

export type EinfuegepunktProps = {
  /** Wird an die Rückrufe durchgereicht: Stelle in `line_rows`. */
  onPosition: () => void;
  onOptional: () => void;
  onBlock: () => void;
  /**
   * Dieselbe Stelle ist auch eine Ablagestelle für Drag&Drop — genau wie im
   * Index (Benjamin, 01.09.2026: „Positionen müssen sich auch in der
   * Gesamtübersicht per Drag&Drop verschieben lassen"). Während irgendeine
   * Zeile gezogen wird, tritt die Plus-Reihe zurück und macht einer
   * Ablagelinie Platz — beides gleichzeitig anzuzeigen wäre zwei
   * widersprüchliche Angebote an derselben Stelle.
   */
  ziehtAktiv?: boolean;
  ablageErlaubt?: boolean;
  onDropZiel?: () => void;
};

export default function InvoiceEinfuegepunkt({
  onPosition, onOptional, onBlock, ziehtAktiv, ablageErlaubt, onDropZiel,
}: EinfuegepunktProps) {
  const [offen, setOffen] = useState(false);
  const [zielAktiv, setZielAktiv] = useState(false);

  if (ziehtAktiv) {
    return (
      <div
        className={"invoice-einfuegen-ablage" + (zielAktiv && ablageErlaubt ? " is-aktiv" : "")}
        onDragOver={(event) => { if (!ablageErlaubt) return; event.preventDefault(); setZielAktiv(true); }}
        onDragLeave={() => setZielAktiv(false)}
        onDrop={(event) => { event.preventDefault(); setZielAktiv(false); if (ablageErlaubt) onDropZiel?.(); }}
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      className={"invoice-einfuegen" + (offen ? " is-offen" : "")}
      onMouseEnter={() => setOffen(true)}
      onMouseLeave={() => setOffen(false)}
      onFocus={() => setOffen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setOffen(false);
      }}
    >
      <span className="invoice-einfuegen-linie" aria-hidden="true" />
      <span className="invoice-einfuegen-knoepfe">
        <button type="button" onClick={onPosition}>+ Position</button>
        <button type="button" onClick={onOptional}>+ Optionale</button>
        <button type="button" onClick={onBlock}>+ Block</button>
      </span>
    </div>
  );
}
